/**
 * Immersive / fullscreen helpers for landscape capture (Samsung Chrome friendly).
 *
 * Strategy:
 * 1. Always apply CSS immersive immediately (visible feedback).
 * 2. Kick off native Fullscreen API in the same synchronous turn as the tap
 *    (prefer pointerdown / touchstart — click is often too late on Android).
 */

const CSS_TARGET = "pi-immersive-on";
const CSS_BODY = "pi-immersive-body";

export function isFullscreenActive() {
  if (typeof document === "undefined") {
    return false;
  }

  return Boolean(
    document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement
  );
}

export function isCssImmersiveActive(element) {
  if (typeof document === "undefined") {
    return false;
  }
  if (document.body?.classList?.contains(CSS_BODY)) {
    return true;
  }
  if (element?.classList?.contains(CSS_TARGET)) {
    return true;
  }
  return Boolean(document.querySelector(`.${CSS_TARGET}`));
}

export function isImmersiveActive(element) {
  return (
    isStandaloneDisplay() ||
    isFullscreenActive() ||
    isCssImmersiveActive(element)
  );
}

export function supportsElementFullscreen() {
  if (typeof document === "undefined") {
    return false;
  }

  const el = document.documentElement;
  return Boolean(
    el.requestFullscreen ||
      el.webkitRequestFullscreen ||
      el.webkitRequestFullScreen ||
      el.mozRequestFullScreen ||
      el.msRequestFullscreen
  );
}

export function isStandaloneDisplay() {
  if (typeof window === "undefined") {
    return false;
  }

  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.matchMedia("(display-mode: fullscreen)").matches ||
    window.navigator.standalone === true
  );
}

function kickoffFullscreen(target) {
  if (!target) return null;

  try {
    if (typeof target.requestFullscreen === "function") {
      try {
        return target.requestFullscreen();
      } catch {
        try {
          return target.requestFullscreen({ navigationUI: "hide" });
        } catch {
          return null;
        }
      }
    }
    if (typeof target.webkitRequestFullscreen === "function") {
      return Promise.resolve(target.webkitRequestFullscreen());
    }
    if (typeof target.webkitRequestFullScreen === "function") {
      target.webkitRequestFullScreen();
      return Promise.resolve();
    }
    if (typeof target.mozRequestFullScreen === "function") {
      target.mozRequestFullScreen();
      return Promise.resolve();
    }
    if (typeof target.msRequestFullscreen === "function") {
      target.msRequestFullscreen();
      return Promise.resolve();
    }
    if (typeof target.webkitEnterFullscreen === "function") {
      target.webkitEnterFullscreen();
      return Promise.resolve();
    }
  } catch {
    return null;
  }

  return null;
}

export function applyCssImmersive(element) {
  if (typeof document === "undefined") {
    return false;
  }

  document.documentElement.classList.add(CSS_BODY);
  document.body.classList.add(CSS_BODY);
  const target =
    element ||
    document.querySelector(".pi-inspection-camera") ||
    document.querySelector(".pi-damage-capture");
  if (target) {
    target.classList.add(CSS_TARGET);
    // Do not pin width/height from visualViewport — on Samsung after rotate
    // that value is often stale and locks the UI to half the screen.
    target.style.width = "";
    target.style.height = "";
    target.style.minHeight = "";
  }
  try {
    window.scrollTo(0, 1);
  } catch {
    /* ignore */
  }
  return true;
}

export function clearCssImmersive() {
  if (typeof document === "undefined") {
    return;
  }

  document.documentElement.classList.remove(CSS_BODY);
  document.body.classList.remove(CSS_BODY);
  document.querySelectorAll(`.${CSS_TARGET}`).forEach((node) => {
    node.classList.remove(CSS_TARGET);
    node.style.height = "";
    node.style.width = "";
    node.style.minHeight = "";
  });
}

async function lockLandscape() {
  try {
    await window.screen?.orientation?.lock?.("landscape");
  } catch {
    /* ignore */
  }
}

/**
 * Start immersive mode from a pointerdown / touchstart / click handler.
 * Must be called synchronously from that handler (no await before it).
 *
 * @returns {Promise<{ ok: boolean, native: boolean, error?: string }>}
 */
export function enterImmersiveMode(element) {
  if (typeof document === "undefined") {
    return Promise.resolve({ ok: false, native: false, error: "No document" });
  }

  const root =
    element ||
    document.querySelector(".pi-inspection-camera") ||
    document.documentElement;

  // Always apply CSS first so Samsung users get immediate feedback even when
  // the Fullscreen API is blocked.
  applyCssImmersive(root);

  if (isStandaloneDisplay() || isFullscreenActive()) {
    return lockLandscape().then(() => ({ ok: true, native: isFullscreenActive() }));
  }

  const video = root?.querySelector?.("video") || null;
  const candidates = [document.documentElement, root, video].filter(Boolean);

  let pending = null;
  for (let i = 0; i < candidates.length; i += 1) {
    pending = kickoffFullscreen(candidates[i]);
    if (pending) break;
  }

  if (!pending) {
    return Promise.resolve({
      ok: true,
      native: false,
      error: "Fullscreen API unavailable",
    });
  }

  return Promise.resolve(pending)
    .then(async () => {
      await lockLandscape();
      return { ok: true, native: true };
    })
    .catch((err) => {
      // Keep CSS immersive; surface why native FS failed.
      const message =
        err?.message ||
        err?.name ||
        "Browser blocked full screen";
      return { ok: true, native: false, error: message };
    });
}

export async function exitImmersiveMode() {
  if (typeof document === "undefined") {
    return false;
  }

  clearCssImmersive();

  if (isFullscreenActive()) {
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        await Promise.resolve(document.webkitExitFullscreen());
      } else if (document.webkitCancelFullScreen) {
        document.webkitCancelFullScreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    } catch {
      /* ignore */
    }
  }

  try {
    window.screen?.orientation?.unlock?.();
  } catch {
    /* ignore */
  }

  return !isImmersiveActive();
}

export function subscribeFullscreenChange(onChange) {
  if (typeof document === "undefined") {
    return () => {};
  }

  const handler = () => onChange(isImmersiveActive());
  const events = [
    "fullscreenchange",
    "webkitfullscreenchange",
    "mozfullscreenchange",
    "MSFullscreenChange",
  ];

  events.forEach((eventName) =>
    document.addEventListener(eventName, handler)
  );

  return () => {
    events.forEach((eventName) =>
      document.removeEventListener(eventName, handler)
    );
  };
}
